// Token + user persistence for the backoffice. localStorage-backed so
// the auth state survives page reloads. A tiny pub/sub lets the
// AuthProvider re-render when the token changes (login, logout, refresh).
//
// Storing JWT in localStorage trades cookie-based CSRF protection for
// XSS exposure. Mitigations enforced elsewhere:
//   - Strict CSP via CloudFront response_headers_policy (PR 2.9).
//   - No `dangerouslySetInnerHTML` anywhere in this app.
//   - Backoffice domain is its own subdomain (no third-party scripts).

const TOKEN_KEY = 'dc-bo-jwt';
const EXP_KEY = 'dc-bo-jwt-exp';
const USER_KEY = 'dc-bo-user';

/** Backoffice scoped-access role. NULL/absent → no scoped access; the user
 *  is either a non-admin or an unassigned admin who needs a role from
 *  super_admin before they can use scoped routes.
 *  Source of truth: apps/api/src/core/auth/permissions.ts (must stay in
 *  sync — list-of-roles, section bundles). Mirrored here so backoffice
 *  can render the sidebar without an extra round trip. */
export type StaffRole =
  | 'super_admin'
  | 'operations'
  | 'finance'
  | 'marketing'
  | 'support'
  | 'viewer';

export interface AuthUser {
  id: number;
  email: string;
  role?: string;
  /** Backoffice scoped role; populated from /auth/me. Null for unassigned
   *  admins (legacy rows before 0093 backfill, or super_admin nulled them). */
  staffRole?: StaffRole | null;
  firstName?: string;
  lastName?: string;
}

type Listener = () => void;

class AuthStore {
  private listeners = new Set<Listener>();

  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOKEN_KEY);
  }

  getUser(): AuthUser | null {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }

  /** Token expiry in seconds since epoch (matches Payload's `exp`). */
  getExp(): number | null {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(EXP_KEY);
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  setToken(token: string, exp?: number): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TOKEN_KEY, token);
    if (exp != null) window.localStorage.setItem(EXP_KEY, String(exp));
    this.emit();
  }

  setUser(user: AuthUser | null): void {
    if (typeof window === 'undefined') return;
    if (user) window.localStorage.setItem(USER_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(USER_KEY);
    this.emit();
  }

  setSession(token: string, user: AuthUser, exp?: number): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TOKEN_KEY, token);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (exp != null) window.localStorage.setItem(EXP_KEY, String(exp));
    this.emit();
  }

  clear(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(EXP_KEY);
    window.localStorage.removeItem(USER_KEY);
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

export const authStore = new AuthStore();
